import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Button, Alert, LinearProgress,
  Chip, Divider, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { AccountBalanceWallet, Lock, LockOpen, Add, Remove } from '@mui/icons-material';
import api from '../services/api';
import type { CashRegisterSummary } from '../types';
import TextField from '../components/common/TextField';

const fmt = (v: number) => `$${(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAYMENT_METHODS = ['Efectivo', 'Tarjeta', 'Transferencia', 'QR', 'Otro'];

export const CajaPage: React.FC = () => {
  const [summary, setSummary] = useState<CashRegisterSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Apertura
  const [openingAmount, setOpeningAmount] = useState('0');
  // Cierre
  const [closeOpen, setCloseOpen] = useState(false);
  const [declaredAmount, setDeclaredAmount] = useState('0');
  const [closeResult, setCloseResult] = useState<CashRegisterSummary | null>(null);
  // Movimiento manual
  const [movOpen, setMovOpen] = useState(false);
  const [movType, setMovType] = useState<'CashIn' | 'CashOut'>('CashIn');
  const [movAmount, setMovAmount] = useState('0');
  const [movMethod, setMovMethod] = useState('Efectivo');
  const [movDesc, setMovDesc] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setSummary(await api.getCurrentCashSession());
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar la caja');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = async () => {
    setBusy(true); setError(null);
    try {
      setSummary(await api.openCashRegister(Number(openingAmount) || 0));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al abrir la caja');
    } finally { setBusy(false); }
  };

  const close = async () => {
    setBusy(true); setError(null);
    try {
      const res = await api.closeCashRegister(Number(declaredAmount) || 0);
      setCloseResult(res);
      setCloseOpen(false);
      setSummary(null); // ya no hay caja abierta
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cerrar la caja');
    } finally { setBusy(false); }
  };

  const addMovement = async () => {
    setBusy(true); setError(null);
    try {
      setSummary(await api.addCashMovement({ type: movType, amount: Number(movAmount) || 0, paymentMethod: movMethod, description: movDesc }));
      setMovOpen(false); setMovAmount('0'); setMovDesc('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al registrar el movimiento');
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AccountBalanceWallet color="primary" />
        <Typography variant="h4" fontWeight={700}>Caja</Typography>
        {summary && <Chip color="success" icon={<LockOpen />} label="Abierta" />}
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Resultado de arqueo tras cerrar */}
      {closeResult && (
        <Alert
          severity={Math.abs(closeResult.difference || 0) < 0.01 ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          onClose={() => setCloseResult(null)}
        >
          Caja cerrada. Esperado {fmt(closeResult.expectedCash)} · Declarado {fmt(closeResult.declaredAmount || 0)} ·{' '}
          Diferencia <b>{fmt(closeResult.difference || 0)}</b>{' '}
          {(closeResult.difference || 0) === 0 ? '(sin diferencia)' : (closeResult.difference || 0) > 0 ? '(sobrante)' : '(faltante)'}.
        </Alert>
      )}

      {/* Caja cerrada → apertura */}
      {!loading && !summary && (
        <Card sx={{ maxWidth: 420 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Abrir caja</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ingresá el monto inicial de efectivo en la caja para empezar a vender.
            </Typography>
            <Stack spacing={2}>
              <TextField label="Monto inicial" type="number" value={openingAmount}
                onChange={e => setOpeningAmount(e.target.value)} autoFocus />
              <Button variant="contained" startIcon={<LockOpen />} onClick={open} disabled={busy}>
                {busy ? 'Abriendo…' : 'Abrir caja'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Caja abierta → resumen + movimientos */}
      {!loading && summary && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={4} flexWrap="wrap" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Apertura</Typography>
                  <Typography variant="h6">{fmt(summary.openingAmount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ventas ({summary.salesCount})</Typography>
                  <Typography variant="h6" color="success.main">{fmt(summary.salesTotal)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ingresos / Egresos</Typography>
                  <Typography variant="h6">{fmt(summary.cashIn)} / {fmt(summary.cashOut)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Efectivo esperado</Typography>
                  <Typography variant="h6" color="primary.main">{fmt(summary.expectedCash)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => { setMovType('CashIn'); setMovOpen(true); }}>Ingreso</Button>
                  <Button variant="outlined" color="warning" startIcon={<Remove />} onClick={() => { setMovType('CashOut'); setMovOpen(true); }}>Egreso</Button>
                  <Button variant="contained" color="error" startIcon={<Lock />} onClick={() => { setDeclaredAmount(String(summary.expectedCash)); setCloseOpen(true); }}>Cerrar caja</Button>
                </Stack>
              </Stack>

              {summary.totalsByMethod.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {summary.totalsByMethod.map(t => (
                      <Chip key={t.paymentMethod} variant="outlined" label={`${t.paymentMethod}: ${fmt(t.amount)} (${t.count})`} />
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Movimientos</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hora</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.movements.map(m => (
                      <TableRow key={m.id}>
                        <TableCell>{new Date(m.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                        <TableCell>
                          <Chip size="small"
                            color={m.type === 'Sale' ? 'success' : m.type === 'CashIn' ? 'info' : 'warning'}
                            label={m.type === 'Sale' ? 'Venta' : m.type === 'CashIn' ? 'Ingreso' : 'Egreso'} />
                        </TableCell>
                        <TableCell>{m.paymentMethod}</TableCell>
                        <TableCell>{m.description || '—'}</TableCell>
                        <TableCell align="right">{m.type === 'CashOut' ? '-' : ''}{fmt(m.amount)}</TableCell>
                      </TableRow>
                    ))}
                    {summary.movements.length === 0 && (
                      <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Sin movimientos.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Dialog movimiento manual */}
      <Dialog open={movOpen} onClose={() => setMovOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{movType === 'CashIn' ? 'Ingreso de efectivo' : 'Egreso de efectivo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Monto" type="number" value={movAmount} onChange={e => setMovAmount(e.target.value)} autoFocus />
            <TextField select label="Método" value={movMethod} onChange={e => setMovMethod(e.target.value)}>
              {PAYMENT_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Descripción" value={movDesc} onChange={e => setMovDesc(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={addMovement} disabled={busy || Number(movAmount) <= 0}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog cierre / arqueo */}
      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cerrar caja (arqueo)</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Efectivo esperado: <b>{fmt(summary?.expectedCash || 0)}</b>. Contá el efectivo real y declaralo.
            </Typography>
            <TextField label="Efectivo contado (declarado)" type="number" value={declaredAmount}
              onChange={e => setDeclaredAmount(e.target.value)} autoFocus />
            <Typography variant="body2">
              Diferencia: <b>{fmt((Number(declaredAmount) || 0) - (summary?.expectedCash || 0))}</b>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={close} disabled={busy}>Cerrar caja</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CajaPage;
